import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Button } from '@workspace/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@workspace/ui/components/card';
import { Input } from '@workspace/ui/components/input';
import { Label } from '@workspace/ui/components/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@workspace/ui/components/select';
import { Skeleton } from '@workspace/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@workspace/ui/components/table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Layout } from '~/components/layouts/layout';
import { m } from '~/paraglide/messages.js';
import { fetchJson } from '~/utils/fetch-json-v2';

export const Route = createFileRoute('/_authenticated/dev/users/')({
  component: DevUsersPage,
});

const LIMIT = 10;

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

interface UsersResult {
  users: Record<string, unknown>[];
  pagination: Pagination;
}

interface UsersFilters {
  status: string;
  search: string;
  urn: string;
}

const statusOptions = [
  { value: 'all', label: m['common.all']() },
  {
    value: 'awaiting_compliance_verification',
    label: m['developer.dash_users.status.awaiting_compliance_verification'](),
  },
  { value: 'active', label: m['common.status.active']() },
];

const toText = (value: unknown, fallback = '-') => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'boolean') return value ? 'Si' : 'No';
  return fallback;
};

const firstValue = (item: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    if (item[key] !== undefined && item[key] !== null) {
      return item[key];
    }
  }

  return null;
};

const getProfile = (user: Record<string, unknown>) =>
  user.profile && typeof user.profile === 'object'
    ? (user.profile as Record<string, unknown>)
    : null;

const userName = (user: Record<string, unknown>) => {
  const profile = getProfile(user);
  const firstName =
    typeof profile?.first_name === 'string' ? profile.first_name.trim() : '';
  const lastName =
    typeof profile?.last_name === 'string' ? profile.last_name.trim() : '';
  const fullName = `${firstName} ${lastName}`.trim();

  if (fullName.length > 0) return fullName;

  return firstValue(user, ['name', 'full_name', 'alias']);
};

const userEmail = (user: Record<string, unknown>) => {
  const profile = getProfile(user);
  if (typeof profile?.email === 'string' && profile.email.trim().length > 0) {
    return profile.email;
  }

  return firstValue(user, ['email']);
};

const toPagination = (
  payload: Record<string, unknown>,
  page: number,
  limit: number,
  total: number,
): Pagination => {
  const data = (payload.pagination ?? {}) as Record<string, unknown>;
  const payloadPage = Number(data.page);
  const payloadLimit = Number(data.limit);
  const payloadTotal = Number(data.total);
  const payloadTotalPages = Number(data.totalPages);

  const safePage = Number.isFinite(payloadPage) ? payloadPage : page;
  const safeLimit = Number.isFinite(payloadLimit) ? payloadLimit : limit;
  const safeTotal = Number.isFinite(payloadTotal) ? payloadTotal : total;
  const calculatedPages = safeLimit > 0 ? Math.ceil(safeTotal / safeLimit) : 1;
  const safeTotalPages = Number.isFinite(payloadTotalPages)
    ? payloadTotalPages
    : Math.max(calculatedPages, 1);

  return {
    page: safePage,
    limit: safeLimit,
    total: safeTotal,
    totalPages: safeTotalPages,
  };
};

const toUsersList = (payload: Record<string, unknown>) => {
  const candidates = [payload.data, payload.users, payload.results];
  const list = candidates.find((value) => Array.isArray(value));
  return Array.isArray(list) ? (list as Record<string, unknown>[]) : [];
};

async function getUsers(
  page: number,
  limit: number,
  filters: UsersFilters,
): Promise<UsersResult> {
  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });

  if (filters.status !== 'all') {
    params.set('status', filters.status);
  }

  if (filters.search.trim().length > 0) {
    params.set('search', filters.search.trim());
  }

  if (filters.urn.trim().length > 0) {
    params.set('urn', filters.urn.trim());
  }

  const result = await fetchJson<Record<string, unknown>>(
    `/v2/dash/users?${params.toString()}`,
  );

  if (result.error || !result.data) {
    throw new Error(result.error ?? 'Error al listar usuarios');
  }

  const payload = result.data;
  const users = toUsersList(payload);

  return {
    users,
    pagination: toPagination(payload, page, limit, users.length),
  };
}

function DevUsersPage() {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [urn, setUrn] = useState('');
  const [filters, setFilters] = useState<UsersFilters>({
    status: 'all',
    search: '',
    urn: '',
  });

  const { data, isPending, error } = useQuery({
    queryKey: [
      'dev-dash-users',
      page,
      LIMIT,
      filters.status,
      filters.search,
      filters.urn,
    ],
    queryFn: () => getUsers(page, LIMIT, filters),
  });

  const users = data?.users ?? [];
  const pagination = data?.pagination ?? {
    page: 1,
    limit: LIMIT,
    total: 0,
    totalPages: 1,
  };

  const handleApplyFilters = () => {
    setFilters({ status, search, urn });
    setPage(1);
  };

  return (
    <Layout
      title={m['developer.dash_users.title']()}
      breadcrumbs={[
        { label: m['developer.menu.title'](), href: '/dev' },
        { label: m['developer.dash_users.title'](), href: '/dev/users' },
      ]}
    >
      <Card>
        <CardHeader>
          <CardTitle>{m['developer.dash_users.title']()}</CardTitle>
          <CardDescription>
            {m['developer.dash_users.subtitle']()}
          </CardDescription>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
            <div className="space-y-2">
              <Label htmlFor="status">
                {m['developer.dash_users.status_label']()}
              </Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger id="status" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="search">{m['common.search']()}</Label>
              <Input
                id="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={m['developer.dash_users.search_placeholder']()}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="urn">{m['developer.dash_users.urn']()}</Label>
              <Input
                id="urn"
                value={urn}
                onChange={(event) => setUrn(event.target.value)}
                placeholder={m['developer.dash_users.urn_placeholder']()}
              />
            </div>

            <div className="space-y-2">
              <Label className="invisible">{m['common.actions']()}</Label>
              <Button onClick={handleApplyFilters} className="w-full">
                {m['developer.dash_users.apply_filters']()}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-red-500">
              {error.message ?? m['developer.dash_users.error_loading']()}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{m['common.name']()}</TableHead>
                      <TableHead>{m['developer.dash_users.email']()}</TableHead>
                      <TableHead>URN</TableHead>
                      <TableHead>
                        {m['developer.dash_users.status_label']()}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {isPending
                      ? Array.from({ length: LIMIT }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            {Array.from({ length: 4 }).map((__, cellIndex) => (
                              <TableCell key={`cell-${cellIndex}`}>
                                <Skeleton className="h-4 w-full" />
                              </TableCell>
                            ))}
                          </TableRow>
                        ))
                      : users.map((user, index) => (
                          <TableRow
                            key={toText(
                              firstValue(user, ['urn', 'id', 'uuid', 'code']),
                              `user-${index}`,
                            )}
                          >
                            <TableCell>{toText(userName(user))}</TableCell>
                            <TableCell>{toText(userEmail(user))}</TableCell>
                            <TableCell className="font-mono text-xs">
                              {toText(firstValue(user, ['urn']))}
                            </TableCell>
                            <TableCell>
                              {toText(firstValue(user, ['status']))}
                            </TableCell>
                          </TableRow>
                        ))}
                    {!isPending && users.length === 0 && (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="text-center text-muted-foreground py-8"
                        >
                          {m['developer.dash_users.empty_message']()}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {m['common.table.page_x_of_y']({
                    current: pagination.page,
                    total: pagination.totalPages,
                  })}{' '}
                  | {m['common.total']()}: {pagination.total}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setPage((current) => Math.max(current - 1, 1))
                    }
                    disabled={pagination.page <= 1 || isPending}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      setPage((current) =>
                        Math.min(current + 1, pagination.totalPages),
                      )
                    }
                    disabled={
                      pagination.page >= pagination.totalPages || isPending
                    }
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
